import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { LanguageToggle } from "../components/ui";
import RSLogo from "../components/icons/RSLogo";
import { ArrowRight } from "../components/icons/svg/outline";
import {
  LANGUAGE_CONFIG,
  UI_COPY,
  loginSmsErrorCopy,
  uiText,
} from "../config";
import { getCookie } from "../utils/dom";
import { completePhoneLogin, startPhoneLogin } from "../services/api/core";
import {
  formatSMSPhoneNumberInput,
  getAuthBackendOrigin,
  getLoginNextUrl,
  getLoginPathLanguage,
  getLoginRouteForLanguage,
  getPersistableLoginLanguage,
  validateSMSPhoneNumber,
} from "../utils/auth";

/**
 * Renders the public top bar for unauthenticated login pages.
 *
 * @param {object} props Component props.
 * @param {Function} props.t Resolves copy for the path language.
 * @returns {JSX.Element} Public login navigation bar.
 */
const LoginHeader = ({ t }) => (
  <header className="h-14 w-full bg-primary text-white shadow-lg shadow-primary-900/20">
    <div className="relative mx-auto flex h-full max-w-6xl items-center justify-between px-4 sm:px-6">
      <div aria-hidden="true" className="w-24" />
      <a
        href="/"
        className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center"
        title={t(UI_COPY.login.returnHome)}
      >
        <RSLogo classes="h-8 ml-auto" variant="logo_trans_white" alt="R&S logo" />
        <p className="ml-2 mr-auto hidden text-lg font-bold sm:flex">
          R&S CONSTRUCTION
        </p>
        <p className="ml-2 mr-auto flex text-lg font-bold sm:hidden">
          R&S
        </p>
      </a>
      <div aria-hidden="true" className="w-24" />
    </div>
  </header>
);

/**
 * Login header: language toggle shares a line with the badge, or with the title
 * when there is no badge.
 *
 * @param {object} props Component props.
 * @param {string} props.language Active path language code.
 * @param {Function} props.onLanguageChange Navigates to `/login/<code>`.
 * @param {Function} props.t Resolves copy for the path language.
 * @param {string} [props.badge] Optional small uppercase label.
 * @param {string} props.title Card heading.
 * @param {string} props.help Instructional copy under the heading.
 * @returns {JSX.Element} Login card header.
 */
const LoginCardHeader = ({
  language,
  onLanguageChange,
  t,
  badge,
  title,
  help,
}) => (
  <>
    <div className="flex items-center justify-between gap-3">
      {badge ? (
        <p className="m-0 text-sm font-semibold uppercase leading-none tracking-wide text-secondary-700">
          {badge}
        </p>
      ) : (
        <h2 className="m-0 min-w-0 text-xl font-bold leading-none text-gray-900">
          {title}
        </h2>
      )}
      <div className="shrink-0 -translate-y-0.5">
        <LanguageToggle
          language={language}
          options={LANGUAGE_CONFIG.OPTIONS}
          onChange={onLanguageChange}
          variant="pill"
          getLabel={(option) => option.code}
          ariaLabel={t(UI_COPY.login.language)}
        />
      </div>
    </div>
    {badge ? (
      <h2 className="mt-2 text-xl font-bold text-gray-900">{title}</h2>
    ) : null}
    <p className="mt-3 min-h-[3.5rem] md:min-h-[2.5rem] text-sm leading-6 text-gray-600">
      {help}
    </p>
  </>
);

/**
 * Renders the SMS code login card.
 *
 * @param {object} props Component props.
 * @param {string} props.phoneNumber Current phone number value.
 * @param {string} props.code Current one-time code value.
 * @param {string} props.step Current SMS login step.
 * @param {string} props.message Status message to show.
 * @param {string} props.errorMessage Error message to show.
 * @param {boolean} props.isSubmitting Whether a phone login request is running.
 * @param {Function} props.t Resolves copy for the path language.
 * @param {string} props.language Active path language code.
 * @param {Function} props.onLanguageChange Navigates to `/login/<code>`.
 * @param {Function} props.onPhoneChange Handles phone input changes.
 * @param {Function} props.onCodeChange Handles code input changes.
 * @param {Function} props.onSendCode Starts phone login.
 * @param {Function} props.onCompleteCode Completes phone login.
 * @param {Function} props.onEditPhone Returns to phone entry.
 * @param {Function} props.onUsePassword Switches to password login.
 * @returns {JSX.Element} SMS code login panel.
 */
const SMSCodeLoginCard = ({
  phoneNumber,
  code,
  step,
  message,
  errorMessage,
  isSubmitting,
  t,
  language,
  onLanguageChange,
  onPhoneChange,
  onCodeChange,
  onSendCode,
  onCompleteCode,
  onEditPhone,
  onUsePassword,
}) => (
  <form
    className="rounded-lg border border-white/20 bg-white/90 p-8 pt-4 shadow-xl shadow-primary-900/10 backdrop-blur"
    onSubmit={step === "phone" ? onSendCode : onCompleteCode}
  >
    <LoginCardHeader
      language={language}
      onLanguageChange={onLanguageChange}
      t={t}
      badge={t(UI_COPY.login.sms.badge)}
      title={t(UI_COPY.login.sms.title)}
      help={t(UI_COPY.login.sms.help)}
    />

    {message ? (
      <div
        className="mt-6 rounded-lg border border-secondary-100 bg-secondary-50 px-4 py-3 text-sm text-secondary-800"
        aria-live="polite"
      >
        {message}
      </div>
    ) : null}

    {errorMessage ? (
      <div
        className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        aria-live="polite"
      >
        {errorMessage}
      </div>
    ) : null}

    <div className="mt-2">
      <label
        className="text-sm font-semibold text-gray-700"
        htmlFor="id_sms_number"
      >
        {t(UI_COPY.login.sms.phoneLabel)}
      </label>
      <input
        className="mt-2 caret-black block min-h-[48px] w-full rounded-lg border border-gray-200 bg-gray-50 px-4 text-base text-gray-950 outline-none transition focus:border-secondary-400 focus:bg-white focus:ring-4 focus:ring-secondary-100"
        type="tel"
        name="sms_number"
        id="id_sms_number"
        placeholder={t(UI_COPY.login.sms.phonePlaceholder)}
        autoComplete="tel"
        value={phoneNumber}
        onChange={onPhoneChange}
        disabled={isSubmitting || step === "code"}
      />
    </div>

    {step === "code" ? (
      <>
        <div className="mt-5">
          <label
            className="text-sm font-semibold text-gray-700"
            htmlFor="id_sms_code"
          >
            {t(UI_COPY.login.sms.codeLabel)}
          </label>
          <input
            className="mt-2 caret-black block min-h-[48px] w-full rounded-lg border border-gray-200 bg-gray-50 px-4 text-base text-gray-950 outline-none transition focus:border-secondary-400 focus:bg-white focus:ring-4 focus:ring-secondary-100"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            name="sms_code"
            id="id_sms_code"
            placeholder={t(UI_COPY.login.sms.codePlaceholder)}
            autoComplete="one-time-code"
            enterKeyHint="done"
            value={code}
            onChange={onCodeChange}
            disabled={isSubmitting}
          />
        </div>
        <div className="mt-4 flex justify-center">
          <button
            className="text-sm font-semibold text-secondary-700 hover:text-secondary-500"
            type="button"
            onClick={onEditPhone}
            disabled={isSubmitting}
          >
            {t(UI_COPY.login.sms.differentPhone)}
          </button>
        </div>
      </>
    ) : null}

    <button
      className={`mt-8 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 text-base font-semibold text-white shadow-lg shadow-primary-900/20 transition hover:bg-primary-300 focus:outline-none focus:ring-4 focus:ring-primary-100 ${isSubmitting ? "opacity-60" : ""}`}
      type="submit"
      disabled={isSubmitting}
    >
      {isSubmitting
        ? t(UI_COPY.login.sms.processing)
        : step === "phone"
          ? t(UI_COPY.login.sms.sendCode)
          : t(UI_COPY.login.sms.logIn)}
      <ArrowRight className="h-5 w-5" />
    </button>

    <div className="mt-6 flex justify-center">
      <button
        className="text-sm font-semibold text-secondary-700 hover:text-secondary-500"
        type="button"
        onClick={onUsePassword}
      >
        {t(UI_COPY.login.sms.usePassword)}
      </button>
    </div>
  </form>
);

/**
 * Renders the React-hosted login page while preserving allauth password login.
 * Language comes from the path only (`/login` or `/login/<code>`).
 * Do not read `preferred_language` here; guests are not signed in yet.
 *
 * @param {object} props Optional login page overrides.
 * @param {string} props.csrfToken CSRF token for the login form POST.
 * @param {string} props.errorMessage Password login error message.
 * @param {string} props.loginValue Previously submitted login value.
 * @param {string} props.passwordResetUrl URL for allauth password reset.
 * @param {string} props.redirectFieldName Allauth redirect field name, usually `next`.
 * @param {string} props.redirectFieldValue Destination to return to after login.
 * @param {string} props.formAction URL the password form posts to.
 * @returns {JSX.Element} Login page component.
 */
const Login = ({
  csrfToken = "",
  errorMessage = "",
  loginValue = "",
  passwordResetUrl: _passwordResetUrl = "/accounts/password/reset/",
  redirectFieldName = "next",
  redirectFieldValue = "",
  formAction = "/accounts/login/",
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const language = getLoginPathLanguage(location.pathname);
  const persistLanguage = getPersistableLoginLanguage(location.pathname);
  const t = useCallback(
    (copy, replacements) => uiText(copy, language, replacements),
    [language]
  );
  const [csrfValue, setCsrfValue] = useState(csrfToken);
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const backendOrigin = getAuthBackendOrigin();
  const queryError = queryParams.get("error");
  const [loginMethod, setLoginMethod] = useState(
    queryError || queryParams.get("login") ? "password" : "sms"
  );
  const [smsPhoneNumber, setSMSPhoneNumber] = useState("");
  const [smsCode, setSMSCode] = useState("");
  const [smsStep, setSMSStep] = useState("phone");
  const [smsMessageCopy, setSMSMessageCopy] = useState(null);
  const [smsErrorCopy, setSMSErrorCopy] = useState(null);
  const [smsErrorRaw, setSMSErrorRaw] = useState("");
  const [isSMSSubmitting, setIsSMSSubmitting] = useState(false);
  const resolvedErrorMessage =
    errorMessage ||
    (queryError === "invalid" ? t(UI_COPY.login.errors.invalid) : "") ||
    (queryError === "inactive" ? t(UI_COPY.login.errors.inactive) : "");
  const resolvedLoginValue = loginValue || queryParams.get("login") || "";
  const resolvedRedirectFieldValue =
    redirectFieldValue || getLoginNextUrl(queryParams.get("next"));
  const resolvedFormAction =
    formAction === "/accounts/login/"
      ? `${backendOrigin}/accounts/login/`
      : formAction;
  const smsMessage = smsMessageCopy ? t(smsMessageCopy) : "";
  const smsError = smsErrorCopy ? t(smsErrorCopy) : smsErrorRaw;

  useEffect(() => {
    if (csrfValue) {
      return;
    }

    fetch(`${backendOrigin}/api/core/csrf`, {
      method: "GET",
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => setCsrfValue(data.csrfToken || getCookie("csrftoken")))
      .catch(() => setCsrfValue(getCookie("csrftoken")));
  }, [backendOrigin, csrfValue]);

  useEffect(() => {
    if (
      loginMethod !== "sms" ||
      smsStep !== "code" ||
      !("OTPCredential" in window) ||
      !navigator.credentials
    ) {
      return;
    }

    const abortController = new AbortController();
    navigator.credentials
      .get({
        otp: { transport: ["sms"] },
        signal: abortController.signal,
      })
      .then((credential) => {
        if (credential?.code) {
          setSMSCode(credential.code);
        }
      })
      .catch(() => {});

    return () => abortController.abort();
  }, [loginMethod, smsStep]);

  /**
   * Navigates to `/login/<code>` without dropping `?next=`.
   *
   * @param {string} nextLanguage Preferred-language code.
   * @returns {void}
   */
  const handleLanguageChange = (nextLanguage) => {
    if (nextLanguage === language) {
      return;
    }
    navigate(getLoginRouteForLanguage(nextLanguage, location.search));
  };

  /**
   * Applies the shared SMS phone formatter as the user types.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event Phone input change event.
   * @returns {void}
   */
  const handleSMSPhoneChange = (event) => {
    const formattedPhoneNumber = formatSMSPhoneNumberInput(
      event.target.value,
      smsPhoneNumber
    );

    setSMSPhoneNumber(formattedPhoneNumber);
    if (validateSMSPhoneNumber(formattedPhoneNumber)) {
      setSMSErrorCopy(null);
      setSMSErrorRaw("");
    }
  };

  /**
   * Stores the current one-time SMS code value.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event Code input change event.
   * @returns {void}
   */
  const handleSMSCodeChange = (event) => {
    setSMSCode(event.target.value);
  };

  /**
   * Requests a Twilio Verify code for the submitted phone number.
   *
   * @param {React.FormEvent<HTMLFormElement>} event Phone login form submit event.
   * @returns {Promise<void>}
   */
  const handleSendSMSCode = async (event) => {
    event.preventDefault();

    if (!validateSMSPhoneNumber(smsPhoneNumber)) {
      setSMSErrorCopy(UI_COPY.login.sms.invalidPhone);
      setSMSErrorRaw("");
      return;
    }

    setIsSMSSubmitting(true);
    setSMSErrorCopy(null);
    setSMSErrorRaw("");
    setSMSMessageCopy(null);

    try {
      await startPhoneLogin(smsPhoneNumber);
      setSMSMessageCopy(UI_COPY.login.sms.sentCode);
      setSMSStep("code");
    } catch (error) {
      setSMSErrorCopy(
        loginSmsErrorCopy(error.response?.data?.message) ||
          UI_COPY.login.sms.sendFailed
      );
      setSMSErrorRaw("");
    } finally {
      setIsSMSSubmitting(false);
    }
  };

  /**
   * Verifies the submitted SMS code and redirects after session login.
   *
   * @param {React.FormEvent<HTMLFormElement>} event Phone login form submit event.
   * @returns {Promise<void>}
   */
  const handleCompleteSMSLogin = async (event) => {
    event.preventDefault();

    if (!smsCode.trim()) {
      setSMSErrorCopy(UI_COPY.login.sms.codeRequired);
      setSMSErrorRaw("");
      return;
    }

    setIsSMSSubmitting(true);
    setSMSErrorCopy(null);
    setSMSErrorRaw("");

    try {
      const response = await completePhoneLogin(
        smsPhoneNumber,
        smsCode.trim(),
        resolvedRedirectFieldValue,
        persistLanguage
      );
      window.location.href = response.redirect || "/app/home";
    } catch (error) {
      setSMSErrorCopy(
        loginSmsErrorCopy(error.response?.data?.message) ||
          UI_COPY.login.sms.codeInvalid
      );
      setSMSErrorRaw("");
    } finally {
      setIsSMSSubmitting(false);
    }
  };

  /**
   * Returns the SMS login card to the phone number entry step.
   *
   * @returns {void}
   */
  const handleEditSMSPhone = () => {
    setSMSStep("phone");
    setSMSCode("");
    setSMSMessageCopy(null);
    setSMSErrorCopy(null);
    setSMSErrorRaw("");
  };

  return (
    <div className="min-h-screen bg-custom-rsbg font-inter text-gray-900">
      <LoginHeader t={t} />

      <main className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,_rgba(22,163,74,0.1),_transparent_36%),radial-gradient(circle_at_bottom_right,_rgba(10,42,13,0.1),_transparent_34%)]" />

        <div className="mx-auto flex min-h-[calc(100vh-7rem)] max-w-2xl flex-col justify-center gap-2 px-2 py-4 sm:px-6">
          <section>
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-950 sm:text-5xl">
                {t(UI_COPY.login.heading)}
              </h1>
            </div>
          </section>

          {loginMethod === "sms" ? (
              <SMSCodeLoginCard
                phoneNumber={smsPhoneNumber}
                code={smsCode}
                step={smsStep}
                message={smsMessage}
                errorMessage={smsError}
                isSubmitting={isSMSSubmitting}
                t={t}
                language={language}
                onLanguageChange={handleLanguageChange}
                onPhoneChange={handleSMSPhoneChange}
                onCodeChange={handleSMSCodeChange}
                onSendCode={handleSendSMSCode}
                onCompleteCode={handleCompleteSMSLogin}
                onEditPhone={handleEditSMSPhone}
                onUsePassword={() => setLoginMethod("password")}
              />
            ) : (
              <form
                className="rounded-lg border border-white/70 bg-white p-4 shadow-2xl shadow-primary-900/15 sm:p-8"
                method="post"
                action={resolvedFormAction}
              >
                <input type="hidden" name="csrfmiddlewaretoken" value={csrfValue} />
                {resolvedRedirectFieldValue ? (
                  <input
                    type="hidden"
                    name={redirectFieldName}
                    value={resolvedRedirectFieldValue}
                  />
                ) : null}
                {persistLanguage ? (
                  <input
                    type="hidden"
                    name="preferred_language"
                    value={persistLanguage}
                  />
                ) : null}

                <LoginCardHeader
                  language={language}
                  onLanguageChange={handleLanguageChange}
                  t={t}
                  title={t(UI_COPY.login.password.title)}
                  help={t(UI_COPY.login.password.help)}
                />

                {resolvedErrorMessage ? (
                  <div
                    className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                    aria-live="polite"
                  >
                    {resolvedErrorMessage}
                  </div>
                ) : null}

                <div className="mt-2">
                  <label
                    className="text-sm font-semibold text-gray-700"
                    htmlFor="id_login"
                  >
                    {t(UI_COPY.login.password.email)}
                  </label>
                  <input
                    className="mt-2 block min-h-[48px] w-full rounded-lg border border-gray-200 bg-gray-50 px-4 text-base text-gray-950 outline-none transition focus:border-secondary-400 focus:bg-white focus:ring-4 focus:ring-secondary-100"
                    type="email"
                    name="login"
                    id="id_login"
                    placeholder={t(UI_COPY.login.password.emailPlaceholder)}
                    autoComplete="username"
                    defaultValue={resolvedLoginValue}
                  />
                </div>

                <div className="mt-5">
                  <label
                    className="text-sm font-semibold text-gray-700"
                    htmlFor="id_password"
                  >
                    {t(UI_COPY.login.password.password)}
                  </label>
                  <input
                    className="mt-2 block min-h-[48px] w-full rounded-lg border border-gray-200 bg-gray-50 px-4 text-base text-gray-950 outline-none transition focus:border-secondary-400 focus:bg-white focus:ring-4 focus:ring-secondary-100"
                    type="password"
                    name="password"
                    id="id_password"
                    placeholder={t(UI_COPY.login.password.passwordPlaceholder)}
                    autoComplete="current-password"
                  />
                </div>

                <button
                  className="mt-8 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 text-base font-semibold text-white shadow-lg shadow-primary-900/20 transition hover:bg-primary-300 focus:outline-none focus:ring-4 focus:ring-primary-100"
                  type="submit"
                  id="login"
                >
                  {t(UI_COPY.login.password.logIn)}
                  <ArrowRight className="h-5 w-5" />
                </button>

                <div className="mt-6 flex flex-col gap-3 text-center text-sm sm:flex-row sm:items-center sm:justify-between sm:text-left">
                  {/* <a href={passwordResetUrl} className="font-semibold text-secondary-700 hover:text-secondary-500 w-full text-center">
                    Forgot Password?
                  </a> */}
                  <p className="hover:text-secondary-500 w-full text-center">
                    {t(UI_COPY.login.password.resetHelp)}
                  </p>
                </div>
                <div className="mt-4 flex justify-center">
                  <button
                    className="text-sm font-semibold text-secondary-700 hover:text-secondary-500"
                    type="button"
                    onClick={() => setLoginMethod("sms")}
                  >
                    {t(UI_COPY.login.password.useSms)}
                  </button>
                </div>
              </form>
            )}
        </div>
      </main>

      <footer className="flex h-14 items-center justify-between px-4 text-xs text-gray-500 sm:px-6">
        <span>{t(UI_COPY.login.footerBrand)}</span>
        <span>{t(UI_COPY.login.footerApp)}</span>
      </footer>
    </div>
  );
};

export default Login;
